import * as RadixTabs from '@radix-ui/react-tabs'
import React, { ReactNode } from 'react'
import { cn } from './cn'

export interface TabsProps {
  value?: string
  defaultValue?: string
  onValueChange?: (value: string) => void
  className?: string
  children: ReactNode
}

/** Radix tabs drawn as a pill track. Compose with TabsList / TabsTrigger / TabsContent. */
export const Tabs = ({ className, children, ...props }: TabsProps) => (
  <RadixTabs.Root className={cn('flex flex-col', className)} {...props}>
    {children}
  </RadixTabs.Root>
)

export const TabsList = ({
  className,
  children,
  'aria-label': ariaLabel,
}: {
  className?: string
  children: ReactNode
  'aria-label'?: string
}) => (
  <RadixTabs.List
    aria-label={ariaLabel}
    className={cn(
      'no-scrollbar flex gap-1 self-start overflow-x-auto rounded-pill border border-hairline bg-surface p-1',
      className
    )}
  >
    {children}
  </RadixTabs.List>
)

export const TabsTrigger = ({
  value,
  disabled,
  className,
  children,
}: {
  value: string
  disabled?: boolean
  className?: string
  children: ReactNode
}) => (
  <RadixTabs.Trigger
    value={value}
    disabled={disabled}
    className={cn(
      'inline-flex h-9 shrink-0 cursor-pointer items-center justify-center gap-2 rounded-pill bg-transparent px-4 font-text text-[14px] font-semibold whitespace-nowrap text-fg-2 transition-colors duration-150',
      'hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus disabled:cursor-not-allowed disabled:opacity-40',
      'data-[state=active]:bg-surface-2 data-[state=active]:text-fg',
      className
    )}
  >
    {children}
  </RadixTabs.Trigger>
)

export const TabsContent = ({
  value,
  className,
  children,
}: {
  value: string
  className?: string
  children: ReactNode
}) => (
  <RadixTabs.Content value={value} className={cn('pt-4 outline-none', className)}>
    {children}
  </RadixTabs.Content>
)
